import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { GitForm } from '@/react/portainer/gitops/GitForm';
import { baseEdgeStackWebhookUrl } from '@/portainer/helpers/webhookHelper';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';

import { FormValues } from '../types';

import { DeploymentSection } from './DeploymentSection';
import { HelmChartPathSection } from './HelmChartPathSection';
import { HelmPerDeviceConfigSection } from './HelmPerDeviceConfigSection';
import { HelmValuesFilesSection } from './HelmValuesFilesSection';
import { HelmInstallOptionsSection } from './HelmInstallOptionsSection';

interface Props {
  webhookId: string;
}

export function HelmGitForm({ webhookId }: Props) {
  const { errors, values, setValues } = useFormikContext<FormValues>();

  // Auto-generate release name based on edge stack name
  useEffect(() => {
    if (values.name) {
      const releaseName = `edge-${values.name}`;
      setValues((prevValues) => ({
        ...prevValues,
        helmReleaseName: releaseName,
      }));
    }
  }, [values.name, setValues]);

  return (
    <>
      <GitForm
        errors={errors?.git}
        value={values.git}
        onChange={(gitValues) =>
          setValues((values) => ({
            ...values,
            git: {
              ...values.git,
              ...gitValues,
            },
          }))
        }
        baseWebhookUrl={baseEdgeStackWebhookUrl()}
        webhookId={webhookId}
        isAutoUpdateVisible={isBE}
        deployMethod="helm"
        environmentType="KUBERNETES"
      />

      <DeploymentSection releaseName={values.helmReleaseName || ''} />

      <HelmChartPathSection
        chartPath={values.helmChartPath || ''}
        gitModel={values.git}
        errors={errors}
        onChartPathChange={(chartPath) =>
          handleChange({ helmChartPath: chartPath })
        }
      />

      <HelmPerDeviceConfigSection
        value={values.helmRelativePath}
        gitModel={values.git}
        errors={errors}
        onChange={(helmRelativePath) => handleChange({ helmRelativePath })}
      />

      <HelmValuesFilesSection
        valuesFiles={values.helmValuesFiles || []}
        gitModel={values.git}
        errors={errors}
        onValuesFileChange={(index, value) => {
          const newValuesFiles = [...values.helmValuesFiles];
          newValuesFiles[index] = value;
          handleChange({ helmValuesFiles: newValuesFiles });
        }}
        relativePath={values.helmRelativePath}
      />

      <HelmInstallOptionsSection />
    </>
  );

  function handleChange(newValues: Partial<FormValues>) {
    setValues((values) => ({
      ...values,
      ...newValues,
    }));
  }
}
